<script setup lang="ts">
import {
  AtomText,
  AtomIcon,
  AtomLoadingSpinner,
} from "@chatfood/design-system";
import { ref } from "vue";
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import { LoyaltyDetail } from "../loyalty-detail";
import { LoyaltyCard } from "../loyalty-card";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useLocale } from "~/composables/use-locale";
import type { IDomainLoyaltyProgram } from "~/domain/loyalty-program";
import { useDirectBackend, useLoyaltyProgramStore } from "#imports";

const css = bemBuilder("loyalty-listing");

const emits = defineEmits<{ selected: [state: boolean]; dismissModal: [] }>();

const { t } = useLocale();

const basket = useBasketStore();
const customer = useCustomerStore();
const loyaltyProgramStore = useLoyaltyProgramStore();

const isFetchingPrograms = ref(true);

const loyaltyPrograms = ref<IDomainLoyaltyProgram[]>();
const loyaltySelected = ref<IDomainLoyaltyProgram>();

const { basketRepo } = useDirectBackend();

async function getLoyaltyPrograms() {
  isFetchingPrograms.value = true;

  try {
    const res = await basketRepo.getLoyaltyPrograms({
      businessId: basket.businessId,
      outletId: basket.outletId,
      orderingMode: basket.orderingMode,
      salesChannel: basket.salesChannel,
    });

    loyaltyPrograms.value = res.data.map((program) => ({
      id: program.id,
      title: program.title,
      photo: program.photo ?? "",
      type: program.type,
      cost: program.cost ?? undefined,
      description: program.description,
      isApplicable: program.isApplicable ?? false,
      expiresAt: program.expiresAt ?? undefined,
      ...(program.type === "discount_amount"
        ? { discount: program.discount }
        : {}),
    })) as IDomainLoyaltyProgram[];
  } catch {
    loyaltyPrograms.value = [];
  } finally {
    isFetchingPrograms.value = false;
  }
}

const onDismissModal = () => {
  emits("dismissModal");
};

getLoyaltyPrograms();

const onSelectLoyalty = (loyalty: IDomainLoyaltyProgram) => {
  loyaltySelected.value = loyalty;
  emits("selected", true);
};

const onDeselectLoyalty = () => {
  loyaltySelected.value = undefined;
  emits("selected", false);
};
</script>

<template>
  <div :class="css('')">
    <template v-if="!loyaltySelected">
      <AtomLoadingSpinner
        v-if="isFetchingPrograms"
        :size="30"
        :class="css('loading')"
      />
      <template v-else>
        <div :class="css('balance-block')">
          <AtomText
            v-if="loyaltyProgramStore.canRedeemPoints"
            :class="css('balance')"
            type="h3"
            data-test="balance"
            color="color-base-0"
          >
            <AtomIcon name="rating-ghost" ignore-color />
            {{ customer.loyalty.wallet.points.balance }}
          </AtomText>
          <AtomText
            v-if="customer.loyalty.wallet.cash.balance.value > 0"
            :class="css('balance')"
            type="h3"
            color="color-base-0"
          >
            <AtomIcon name="dollar-sign" ignore-color />
            {{
              toMoney(
                customer.loyalty.wallet.cash.balance.value,
                customer.loyalty.wallet.cash.balance.currency,
              )
            }}
          </AtomText>
        </div>
        <ul v-if="loyaltyPrograms?.length" :class="css('list')">
          <li v-for="item in loyaltyPrograms" :key="item.id" data-test="item">
            <LoyaltyCard v-bind="item" @click="onSelectLoyalty(item)" />
          </li>
        </ul>
        <AtomText v-else tag="div" data-test="empty" :class="css('empty')">
          {{ t("components.loyalty_listing.no_loyalty_available") }}
        </AtomText>
      </template>
    </template>
    <LoyaltyDetail
      v-else
      :loyalty="loyaltySelected"
      @back="onDeselectLoyalty"
      @close="onDismissModal"
    />
  </div>
</template>

<style lang="scss" scoped>
.loyalty-listing {
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 300px;

  &__loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &__balance {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-md);
  }

  &__empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-md) var(--spacing-md) var(--spacing-2xl);
  }

  &__list {
    display: grid;
    list-style: none;
    gap: var(--spacing-md) var(--spacing-lg);
    grid-template-columns: 1fr 1fr;
    padding: var(--spacing-md);
    margin: 0;
    max-height: 70vh;
    overflow-y: auto;

    @media (width > $breakpoint-sm) {
      max-height: 55vh;
    }
  }

  &__balance-block {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}
</style>
